import jsPDF from "jspdf";
import headerImg from "../assets/images/headerExport.jpg";
import footerImg from "../assets/images/footerExport.jpg";

export const exportCasesToPDF = (cases, orientation = 'portrait') => {
  console.log(cases, 'cases data');
  console.log('PDF Orientation:', orientation);

  const doc = new jsPDF({
    orientation: orientation,
    unit: 'mm',
    format: 'a4'
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  let y = 55;

  // Color palette
  const colors = {
    primary: [0, 82, 204],
    secondary: [52, 73, 94],
    accent: [231, 76, 60],
    success: [39, 174, 96],
    background: [248, 249, 250],
    text: [44, 62, 80],
    border: [189, 195, 199],
    headerBg: [41, 128, 185],
    altRow: [236, 240, 241]
  };

  const addHeaderFooter = (pageNum, totalPages) => {
    // Header
    doc.addImage(headerImg, "JPG", 0, 0, pageWidth, 30);

    // Footer
    doc.addImage(footerImg, "JPG", 0, pageHeight - 20, pageWidth, 20);

    // Page number
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...colors.text);
    doc.text(`Page ${pageNum} of ${totalPages}`, pageWidth - 30, pageHeight - 25);
  };

  // Calculate total pages
  const casesPerPage = orientation === 'landscape' ? 1.5 : 1.2;
  const estimatedPagesNeeded = Math.ceil(cases.length / casesPerPage) + 1;
  let currentPage = 1;

  addHeaderFooter(currentPage, estimatedPagesNeeded);

  // Title section
  doc.setFontSize(24);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...colors.primary);
  const title = "Cases Detail Report";
  const titleWidth = doc.getTextWidth(title);
  doc.text(title, (pageWidth - titleWidth) / 2, 42);

  // Subtitle with date (removed format reference)
  doc.setFontSize(12);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...colors.secondary);
  const subtitle = `Generated on ${new Date().toLocaleDateString('en-IN', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })}`;
  const subtitleWidth = doc.getTextWidth(subtitle);
  doc.text(subtitle, (pageWidth - subtitleWidth) / 2, 50);

  // Summary section
  doc.setFontSize(11);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...colors.text);
  doc.text(`Total Cases: ${cases.length}`, 20, y);
  y += 10;

  // Draw separator line
  doc.setDrawColor(...colors.border);
  doc.setLineWidth(0.5);
  doc.line(20, y, pageWidth - 20, y);
  y += 15;

  const marginLeft = 20;
  const marginRight = 20;
  const availableWidth = pageWidth - marginLeft - marginRight;

  // Process each case
  cases.forEach((caseData, index) => {
    console.log(`Processing Case #${index + 1}: ${caseData.caseTypeAndNumber || 'Unknown'} at y=${y}`);
    
    // Check if we need a new page for the case header
    const minSpaceNeeded = orientation === 'landscape' ? 60 : 50;
    if (y + minSpaceNeeded > pageHeight - 35) {
      console.log(`Moving to new page for Case #${index + 1}`);
      doc.addPage();
      currentPage++;
      addHeaderFooter(currentPage, estimatedPagesNeeded);
      y = 55;
    }
    
    // Case header with background
    console.log(`Drawing header for Case #${index + 1}`);
    doc.setFillColor(...colors.headerBg);
    doc.rect(marginLeft, y - 5, availableWidth, 15, 'F');

    doc.setFontSize(orientation === 'landscape' ? 12 : 14);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(255, 255, 255);

    // Case title with proper truncation
    const caseTitle = `Case #${index + 1}: ${String(caseData.caseTypeAndNumber || 'N/A')}`;
    const maxTitleWidth = availableWidth - 100;
    const truncatedTitle = doc.getTextWidth(caseTitle) > maxTitleWidth
      ? caseTitle.substring(0, orientation === 'landscape' ? 60 : 70) + '...'
      : caseTitle;

    doc.text(truncatedTitle, marginLeft + 5, y + 5);

    // Status badge with proper positioning
    const status = String(caseData.caseStatus?.["Case Status"] || 'N/A');
    const statusColor = status.includes('DISPOSED') ? colors.success :
      status.includes('PENDING') ? colors.accent : colors.secondary;

    doc.setFontSize(9);
    doc.setFont("helvetica", "bold");

    const textWidth = doc.getTextWidth(status);
    const padding = 8;
    const boxWidth = Math.min(textWidth + padding, 60); // Max width to prevent overflow
    const boxHeight = 10;
    const badgeX = pageWidth - boxWidth - 25;
    const badgeY = y - 2;

    doc.setFillColor(...statusColor);
    doc.roundedRect(badgeX, badgeY, boxWidth, boxHeight, 3, 3, 'F');

    doc.setTextColor(255, 255, 255);
    // Truncate status text if too long
    const truncatedStatus = doc.getTextWidth(status) > boxWidth - 6 
      ? status.substring(0, 8) + '...' 
      : status;
    doc.text(truncatedStatus, badgeX + 4, y + 3);

    y += 18;

    // Case details
    const details = [
      { label: 'Parties', value: String(caseData.parties || 'N/A') },
      { label: 'CNR Number', value: String(caseData.caseDetails?.["CNR Number"] || 'N/A') },
      { label: 'Filing Number', value: String(caseData.caseDetails?.["Filing Number"] || 'N/A') },
      { label: 'Filing Date', value: String(caseData.caseDetails?.["Filing Date"] || 'N/A') },
      { label: 'Registration Number', value: String(caseData.caseDetails?.["Registration Number"] || 'N/A') },
      { label: 'Registration Date', value: String(caseData.caseDetails?.["Registration Date"] || 'N/A') },
      { label: 'First Hearing Date', value: String(caseData.caseStatus?.["First Hearing Date"] || 'N/A') },
      { label: 'Decision Date', value: String(caseData.caseStatus?.["Decision Date"] || 'N/A') },
      { label: 'Case Status', value: String(caseData.caseStatus?.["Case Status"] || 'N/A') },
      { label: 'Nature of Disposal', value: String(caseData.caseStatus?.["Nature of Disposal"] || 'N/A') },
      { label: 'Coram', value: String(caseData.caseStatus?.["Coram"] || 'N/A') },
      { label: 'Bench Type', value: String(caseData.caseStatus?.["Bench Type"] || 'N/A') },
      { label: 'Judicial Branch', value: String(caseData.caseStatus?.["Judicial Branch"] || 'N/A') },
      { label: 'State', value: String(caseData.caseStatus?.["State"] || 'N/A') },
      { label: 'District', value: String(caseData.caseStatus?.["District"] || 'N/A') },
      { label: 'Advocates', value: String(caseData.advocates || 'N/A') },
      { label: 'Petitioner', value: String(caseData.petitionerAndAdvocate?.["petitioner"] || 'N/A') },
      { label: 'Petitioner Advocate', value: String(caseData.petitionerAndAdvocate?.["advocate"] || 'N/A') },
      { label: 'Respondent', value: String(caseData.petitionerAndAdvocate?.["respondent"] || 'N/A') },
      { label: 'Respondent Advocate', value: String(caseData.petitionerAndAdvocate?.["respondentAdvocate"] || 'N/A') },
      { label: 'Category', value: String(caseData.categoryDetails?.["Category"] || 'N/A') },
      { label: 'Sub Category', value: String(caseData.categoryDetails?.["Sub Category"] || 'N/A') },
      { label: 'Under Act', value: String(caseData.actsData?.[0]?.["underAct"] || 'N/A') },
      { label: 'Under Section', value: String(caseData.actsData?.[0]?.["underSection"] || 'N/A') },
      { label: 'Court', value: String(caseData.court || 'N/A') },
      { label: 'Serial Number', value: String(caseData.serialNumber || 'N/A') },
      { label: 'Case Type & Number', value: String(caseData.caseTypeAndNumber || 'N/A') },
      { label: 'Latest Hearing Date', value: String(caseData.caseHistory?.[0]?.["hearingDate"] || 'N/A') },
      { label: 'Latest Purpose of Hearing', value: String(caseData.caseHistory?.[0]?.["purposeOfHearing"] || 'N/A') },
      { label: 'Latest Business On Date', value: String(caseData.caseHistory?.[0]?.["businessOnDate"] || 'N/A') },
      { label: 'Latest Judge', value: String(caseData.caseHistory?.[0]?.["judge"] || 'N/A') }
    ];

    // Filter out N/A values for more concise output
    const relevantDetails = details.filter(detail => 
      detail.value !== 'N/A' || 
      ['Parties', 'CNR Number', 'Filing Date', 'Case Status', 'Coram'].includes(detail.label)
    );

    if (orientation === 'landscape') {
      // Improved two-column layout for landscape
      const columnWidth = (availableWidth - 15) / 2; // 15mm gap between columns
      const leftColumnX = marginLeft;
      const rightColumnX = marginLeft + columnWidth + 15;
      
      let currentRowY = y;
      let leftColumnY = currentRowY;
      let rightColumnY = currentRowY;
      let currentColumn = 'left';

      relevantDetails.forEach((detail, detailIndex) => {
        const isLeftColumn = currentColumn === 'left';
        const columnX = isLeftColumn ? leftColumnX : rightColumnX;
        const maxValueWidth = columnWidth - 50; // Account for label width
        
        // Split text to handle long values
        const valueLines = doc.splitTextToSize(detail.value, maxValueWidth);
        const rowHeight = Math.max(6, valueLines.length * 4);
        
        // Check if we need to move to next page
        const nextY = isLeftColumn ? leftColumnY : rightColumnY;
        if (nextY + rowHeight > pageHeight - 40) {
          doc.addPage();
          currentPage++;
          addHeaderFooter(currentPage, estimatedPagesNeeded);
          leftColumnY = rightColumnY = y = 55;
        }

        const currentY = isLeftColumn ? leftColumnY : rightColumnY;

        // Alternate row background
        if (detailIndex % 4 < 2) { // Every 2 rows alternate
          doc.setFillColor(...colors.altRow);
          doc.rect(columnX, currentY - 1, columnWidth, rowHeight, 'F');
        }

        // Label
        doc.setFontSize(8);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(...colors.secondary);
        doc.text(detail.label + ':', columnX + 2, currentY + 4);

        // Value
        doc.setFont("helvetica", "normal");
        doc.setTextColor(...colors.text);
        
        if (valueLines.length > 1) {
          valueLines.forEach((line, lineIndex) => {
            doc.text(String(line), columnX + 45, currentY + 4 + (lineIndex * 3.5));
          });
        } else {
          doc.text(String(valueLines[0] || ''), columnX + 45, currentY + 4);
        }

        // Update column position
        if (isLeftColumn) {
          leftColumnY += rowHeight;
          currentColumn = 'right';
        } else {
          rightColumnY += rowHeight;
          currentColumn = 'left';
        }
      });

      // Update y to the maximum of both columns
      y = Math.max(leftColumnY, rightColumnY) + 5;

    } else {
      // Portrait mode - single column with better spacing
      const labelWidth = 60;
      let currentY = y;

      relevantDetails.forEach((detail, detailIndex) => {
        const maxWidth = availableWidth - labelWidth - 10;
        const valueLines = doc.splitTextToSize(detail.value, maxWidth);
        const rowHeight = Math.max(7, valueLines.length * 4 + 2);

        // Check if this row would overflow the page
        if (currentY + rowHeight > pageHeight - 40) {
          doc.addPage();
          currentPage++;
          addHeaderFooter(currentPage, estimatedPagesNeeded);
          currentY = 55;
        }

        // Alternate row background
        if (detailIndex % 2 === 0) {
          doc.setFillColor(...colors.altRow);
          doc.rect(marginLeft, currentY - 1, availableWidth, rowHeight, 'F');
        }

        // Label
        doc.setFontSize(9);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(...colors.secondary);
        doc.text(detail.label + ':', marginLeft + 3, currentY + 4);

        // Value
        doc.setFont("helvetica", "normal");
        doc.setTextColor(...colors.text);

        if (valueLines.length > 1) {
          valueLines.forEach((line, lineIndex) => {
            doc.text(String(line), marginLeft + labelWidth + 5, currentY + 4 + (lineIndex * 3.5));
          });
        } else {
          doc.text(String(detail.value), marginLeft + labelWidth + 5, currentY + 4);
        }

        currentY += rowHeight;
      });

      y = currentY;
    }

    // Case separator
    if (index < cases.length - 1) {
      doc.setDrawColor(...colors.border);
      doc.setLineWidth(0.3);
      doc.line(marginLeft, y + 8, pageWidth - marginRight, y + 8);
      y += 18;
    }
  });

  // Final summary footer
  if (y > pageHeight - 60) {
    doc.addPage();
    currentPage++;
    addHeaderFooter(currentPage, estimatedPagesNeeded);
    y = 55;
  } else {
    y = Math.max(y + 20, pageHeight - 55);
  }

  // Summary statistics
  const disposedCases = cases.filter(c => c.caseStatus?.["Case Status"]?.includes('DISPOSED')).length;
  const pendingCases = cases.length - disposedCases;

  doc.setFillColor(...colors.background);
  doc.rect(marginLeft, y, availableWidth, 30, 'F');
  doc.setDrawColor(...colors.border);
  doc.rect(marginLeft, y, availableWidth, 30, 'S');

  doc.setFontSize(12);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...colors.secondary);
  doc.text("Report Summary", marginLeft + 5, y + 10);

  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...colors.text);
  doc.text(`Total Cases: ${cases.length}`, marginLeft + 5, y + 18);
  doc.text(`Disposed: ${disposedCases}`, marginLeft + 5, y + 24);
  doc.text(`Pending: ${pendingCases}`, marginLeft + 80, y + 24);

  // Report generation info
  doc.setFontSize(8);
  doc.setTextColor(...colors.secondary);
  const generationInfo = `Generated on ${new Date().toLocaleString('en-IN')}`;
  doc.text(generationInfo, pageWidth - doc.getTextWidth(generationInfo) - 10, y + 28);

  // Save the PDF
  const filename = `cases_detail_report_${orientation}_${new Date().toISOString().split('T')[0]}.pdf`;
  doc.save(filename);
};